import * as THREE from 'three';
import { MAVERICK, AGM65 } from '../core/SimConfig.js';
import { WeaponMode } from './WeaponSystem.js';
import maverickTvFrag from '../../glsl/maverick_tv_frag.glsl';
import { FX_OVERLAY_LAYER } from '../fx/FXOverlayPass.js';

// Dedicated render layer for white-hot enemy targets in the IR pass. Enemy
// meshes get this layer enabled (in addition to the default layer 0); the IR
// "hot" render pass points the seeker camera at only this layer.
const HOT_LAYER = 2;

// Note the logdepthbuf chunks: the renderer uses logarithmicDepthBuffer, so this
// in-scene material must emit matching depth or the plane is depth-rejected.
const TV_VERT = `
  #include <common>
  #include <logdepthbuf_pars_vertex>
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    #include <logdepthbuf_vertex>
  }
`;

// ── Scratch (zero-alloc hot path) ────────────────────────────────────────────
const _origin = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _right = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _perp = new THREE.Vector3();
const _targetDir = new THREE.Vector3(); // mouse-driven goal direction (seeker pans toward it)
const _axis = new THREE.Vector3();      // rotation axis for the constant-speed pan step
const _tp = new THREE.Vector3();
const _proj = new THREE.Vector3();     // locked target → NDC (lock-marker projection)
const _toTarget = new THREE.Vector3(); // camera → locked target (front-of-camera test)
const _clearColor = new THREE.Color();
const WORLD_UP = new THREE.Vector3(0, 1, 0);

/**
 * AGM-65 Maverick seeker + TV display.
 *
 * Owns the player-aimed, gimbal-limited, ground-stabilized seeker line-of-sight,
 * the offscreen render of the seeker's view (IR white-hot imaging), the cockpit
 * `_TV` plane it composites onto, and the fire / auto-switch orchestration.
 *
 * The seeker tracks a world-space point on the terrain (`losPoint`). While not
 * aiming it follows the aircraft boresight; while aiming it stays fixed in the
 * world (so aircraft motion is compensated) and the mouse slews it across the
 * ground within the gimbal cone.
 */
export default class MaverickSeeker {
  constructor({ scene, aircraft, model, weaponSystem, cameraManager, terrainCollider, enemyManager }) {
    this.scene = scene;
    this.aircraft = aircraft;
    this.weaponSystem = weaponSystem;
    this.cameraManager = cameraManager;
    this.terrainCollider = terrainCollider;
    this.enemyManager = enemyManager;

    // ── Seeker state ─────────────────────────────────────────────────────
    this.aiming = false;                 // true while RMB-held aim mode is engaged
    this.active = false;                 // Maverick selected + a missile still mounted
    this.inRange = false;                // target within fire range + gimbal (can fire)
    this.atGimbalLimit = false;          // seeker pinned to the gimbal cone edge
    this.losPoint = new THREE.Vector3(); // world-space aim point (ground)
    this._targetPoint = new THREE.Vector3(); // mouse-driven goal the seeker pans toward
    this.range = Infinity;               // metres, seeker → losPoint
    this.lockedEntity = null;            // enemy the missile would track (drives the lock marker)
    this._lockUv = new THREE.Vector2();  // locked target's plane-space UV on the feed

    this.fov = MAVERICK.fovDefault;
    this._targetFov = MAVERICK.fovDefault;
    this._pendingSlewX = 0;
    this._pendingSlewY = 0;
    this._crossGap = MAVERICK.crosshair.gapPx;
    this._cockpitActive = false;
    this._shouldRenderTV = false;

    // ── Offscreen render: base scene (colour) + hot enemies (white on black) ──
    // Both targets share one depth texture: the base pass fills it with the full
    // scene's depth, and the hot pass reuses it (without clearing depth) so
    // enemies behind terrain/buildings are depth-rejected — i.e. white-hot
    // targets are correctly occluded by the world.
    const res = MAVERICK.rtResolution;
    this._depthTexture = new THREE.DepthTexture(res, res);
    this.rtBase = new THREE.WebGLRenderTarget(res, res, { depthTexture: this._depthTexture });
    this.rtHot = new THREE.WebGLRenderTarget(res, res, { depthTexture: this._depthTexture });

    this.seekerCamera = new THREE.PerspectiveCamera(this.fov, 1, 1, 30000);

    // Flat-white override used to paint enemies in the hot pass (fog off so
    // distant targets stay pure white).
    this._whiteMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, fog: false });
    this._hotTagged = new WeakSet();

    // ── `_TV` plane material ─────────────────────────────────────────────
    this.tvMesh = model.getObjectByName(MAVERICK.nodeTV) || null;
    if (!this.tvMesh) {
      console.warn(`MaverickSeeker: "${MAVERICK.nodeTV}" mesh not found in model`);
    } else {
      this._originalTvMaterial = this.tvMesh.material;
      const ir = MAVERICK.ir;
      const cr = MAVERICK.crosshair;
      const lm = MAVERICK.lockMarker;
      const rl = MAVERICK.refLines;
      this.tvMaterial = new THREE.ShaderMaterial({
        uniforms: {
          tBase: { value: this.rtBase.texture },
          tHot: { value: this.rtHot.texture },
          uResolution: { value: new THREE.Vector2(res, res) },
          uTime: { value: 0 },
          uGain: { value: ir.gain },
          uGamma: { value: ir.gamma },
          uColdFloor: { value: ir.coldFloor },
          uHotColor: { value: new THREE.Color().fromArray(ir.hotColor) },
          uScanline: { value: ir.scanlineStrength },
          uNoise: { value: ir.noiseStrength },
          uCrossColor: { value: new THREE.Color().fromArray(cr.color) },
          uCrossHalfWidth: { value: cr.lineWidthPx * 0.5 },
          uCrossGap: { value: this._crossGap },
          uRefColor: { value: new THREE.Color().fromArray(rl.color) },
          uRefHalfWidth: { value: rl.lineWidthPx * 0.5 },
          uRefHalfLen: { value: cr.gapPx },
          uRefSpan: { value: rl.spanFrac * res * 0.5 },
          uRefCount: { value: rl.count },
          uLockActive: { value: 0 },
          uLockUv: { value: this._lockUv },
          uLockColor: { value: new THREE.Color().fromArray(lm.color) },
          uLockHalfWidth: { value: lm.lineWidthPx * 0.5 },
          uLockSize: { value: lm.sizePx },
        },
        vertexShader: TV_VERT,
        fragmentShader: maverickTvFrag,
        side: THREE.DoubleSide,
        extensions: { derivatives: true }, // fwidth() for crosshair antialiasing
      });
      this.tvMesh.material = this.tvMaterial;
      this.tvMesh.visible = false;
    }
  }

  // ── Input hooks (called from Controller) ─────────────────────────────────

  /** Engage aim mode (RMB held while Maverick selected with rounds remaining). */
  enterAim() {
    if (!this.canAim) return false;
    this.aiming = true;
    // losPoint already holds the boresight ground point from the centered
    // update, so the seeker "locks to the point it's currently looking at".
    // Seed the pan target there too — with no mouse input the seeker sits still.
    this._targetPoint.copy(this.losPoint);
    return true;
  }

  /** Leave aim mode — seeker re-centres and zoom returns to default. */
  exitAim() {
    this.aiming = false;
    this._targetFov = MAVERICK.fovDefault;
    this._pendingSlewX = 0;
    this._pendingSlewY = 0;
  }

  /** Accumulate raw mouse motion to slew the seeker (applied in update). */
  slew(dx, dy) {
    this._pendingSlewX += dx;
    this._pendingSlewY += dy;
  }

  /** Zoom the optic in (notch < 0) / out (notch > 0) within the FOV limits. */
  zoom(deltaY) {
    const f = deltaY < 0 ? MAVERICK.zoomStep : 1 / MAVERICK.zoomStep;
    this._targetFov = THREE.MathUtils.clamp(this._targetFov * f, MAVERICK.fovMin, MAVERICK.fovMax);
  }

  /** True when the Maverick is selected and at least one round is mounted. */
  get canAim() {
    return this.weaponSystem?.mode === WeaponMode.AGM65 && this.weaponSystem.mavericksRemaining > 0;
  }

  // ── Fire ─────────────────────────────────────────────────────────────────

  /**
   * Fire the next mounted Maverick at the current aim point. Locks a nearby
   * enemy if one sits under the reticle, else designates the static ground
   * point. Auto-switches to the next round (which inherits the same aim point);
   * leaves aim mode when the rack runs dry. Returns true if a round launched.
   */
  fire(aircraft) {
    if (!this.aiming || !this.inRange) return false;

    const entity = this._nearestEnemyTo(this.losPoint, AGM65.lockAcquireRadius);
    const res = this.weaponSystem.tryFireMaverick(aircraft, entity, this.losPoint);
    if (!res.fired) return false;

    if (res.remaining <= 0) this.exitAim();
    return true;
  }

  // ── Per-frame update ─────────────────────────────────────────────────────

  update(dt) {
    const ws = this.weaponSystem;
    const mounted = ws && ws.mode === WeaponMode.AGM65 ? ws.nextMountedMaverick : null;
    this.active = !!mounted;

    // Cockpit view gate for the (expensive) TV render.
    this._cockpitActive = !!this.cameraManager?.cockpitView;

    if (!this.active) {
      this.aiming = false;
      this.inRange = false;
      this.lockedEntity = null;
      this._shouldRenderTV = false;
      this._targetFov = MAVERICK.fovDefault;
      this.fov = MAVERICK.fovDefault;
      if (this.tvMesh) this.tvMesh.visible = false;
      return;
    }

    // ── Zoom: ramp linearly toward the target FOV at a constant rate ─────
    const dFov = this._targetFov - this.fov;
    const zoomStep = MAVERICK.zoomSpeed * dt;
    if (Math.abs(dFov) <= zoomStep) this.fov = this._targetFov;
    else this.fov += Math.sign(dFov) * zoomStep;

    // ── Seeker origin = the next-to-fire Maverick's pylon ────────────────
    mounted.pylonNode.getWorldPosition(_origin);

    // ── Aim direction ────────────────────────────────────────────────────
    _fwd.copy(this.aircraft.forwardVector);
    const maxRay = MAVERICK.fireRangeMeters * 1.5;
    if (this.aiming) {
      // Current seeker direction, derived from the persisted world point (so it
      // stays glued to the ground as the aircraft flies).
      _dir.copy(this.losPoint).sub(_origin);
      if (_dir.lengthSq() < 1e-6) _dir.copy(_fwd); else _dir.normalize();

      // Mouse slews the *target* aim point (proportional to motion), likewise
      // re-derived from its persisted world point each frame for stabilisation.
      _targetDir.copy(this._targetPoint).sub(_origin);
      if (_targetDir.lengthSq() < 1e-6) _targetDir.copy(_dir); else _targetDir.normalize();

      if (this._pendingSlewX !== 0 || this._pendingSlewY !== 0) {
        const sens = MAVERICK.slewSensitivity * (this.fov / MAVERICK.fovDefault);
        _targetDir.applyAxisAngle(WORLD_UP, -this._pendingSlewX * sens);
        _right.crossVectors(_targetDir, WORLD_UP);
        if (_right.lengthSq() > 1e-6) {
          _right.normalize();
          _targetDir.applyAxisAngle(_right, -this._pendingSlewY * sens);
        }
        _targetDir.normalize();
        this._pendingSlewX = 0;
        this._pendingSlewY = 0;
        // Re-anchor the target to a world ground point so it holds still under
        // aircraft motion until the next mouse input.
        const tHit = this.terrainCollider
          ? this.terrainCollider.raycast(_origin.x, _origin.y, _origin.z, _targetDir.x, _targetDir.y, _targetDir.z, maxRay, 40)
          : null;
        if (tHit) this._targetPoint.set(tHit.x, tHit.y, tHit.z);
        else this._targetPoint.copy(_origin).addScaledVector(_targetDir, maxRay);
      }

      // Pan the seeker toward the target at a constant angular speed (zoom-
      // scaled), capped so it never overshoots — this is the linear "glide".
      const ang = _dir.angleTo(_targetDir);
      if (ang > 1e-6) {
        const step = MAVERICK.panSpeed * (this.fov / MAVERICK.fovDefault) * dt;
        _axis.crossVectors(_dir, _targetDir);
        if (_axis.lengthSq() > 1e-8) {
          _axis.normalize();
          _dir.applyAxisAngle(_axis, Math.min(step, ang)).normalize();
        } else {
          _dir.copy(_targetDir); // (anti)parallel — no well-defined axis
        }
      }
    } else {
      // Centred: follow the aircraft boresight (where the HUD reticle sits).
      _dir.copy(_fwd);
    }

    // ── Gimbal clamp (relative to aircraft boresight) ────────────────────
    this.atGimbalLimit = false;
    const cosLimit = Math.cos(MAVERICK.gimbalLimitDeg * Math.PI / 180);
    const cosA = _dir.dot(_fwd);
    if (cosA < cosLimit) {
      // Rebuild the direction on the cone edge: fwd·cos(limit) + perp·sin(limit).
      _perp.copy(_dir).addScaledVector(_fwd, -cosA);
      if (_perp.lengthSq() > 1e-8) {
        _perp.normalize();
        const sinLimit = Math.sqrt(Math.max(0, 1 - cosLimit * cosLimit));
        _dir.copy(_fwd).multiplyScalar(cosLimit).addScaledVector(_perp, sinLimit).normalize();
        this.atGimbalLimit = true;
      }
    }

    // ── Resolve the ground aim point along the (clamped) direction ───────
    const hit = this.terrainCollider
      ? this.terrainCollider.raycast(_origin.x, _origin.y, _origin.z, _dir.x, _dir.y, _dir.z, maxRay, 40)
      : null;
    if (hit) {
      this.losPoint.set(hit.x, hit.y, hit.z);
      this.range = _origin.distanceTo(this.losPoint);
    } else {
      // No terrain under the LOS (aimed above the horizon) — park the point far
      // out so the seeker still has something to look at, but it's never in range.
      this.losPoint.copy(_origin).addScaledVector(_dir, maxRay);
      this.range = Infinity;
    }

    this.inRange = this.aiming && !this.atGimbalLimit && this.range <= MAVERICK.fireRangeMeters;

    // ── Crosshair gap easing (closes when in range) ──────────────────────
    const cr = MAVERICK.crosshair;
    const targetGap = this.inRange ? cr.inRangeGapPx : cr.gapPx;
    const gapAlpha = 1 - Math.exp(-cr.gapBlendRate * dt);
    this._crossGap += (targetGap - this._crossGap) * gapAlpha;

    // ── Seeker camera pose ───────────────────────────────────────────────
    // The real Maverick seeker gimbals in pitch/yaw but its roll is bolted to
    // the missile body (and thus the airframe) — it is NOT horizon-stabilised.
    // So the feed's "up" follows the aircraft body-up, and the picture cants
    // with bank, exactly as a pilot sees it on the TVM/MFCD before launch.
    this.seekerCamera.position.copy(_origin);
    this.seekerCamera.up.copy(this.aircraft.upVector);
    this.seekerCamera.lookAt(this.losPoint);
    this.seekerCamera.fov = this.fov;
    this.seekerCamera.updateProjectionMatrix();
    // The seeker camera lives outside the scene graph, so the renderer only
    // refreshes its world/inverse matrices later (in renderTV). Refresh them now
    // so this frame's lock-marker projection uses the current pose, not last
    // frame's — otherwise the marker lags the feed by one frame.
    this.seekerCamera.updateMatrixWorld();
    this.seekerCamera.matrixWorldInverse.copy(this.seekerCamera.matrixWorld).invert();

    // ── Lock marker: track the enemy the missile would chase if fired ────
    // Mirrors fire()'s lock test (nearest enemy within lockAcquireRadius of the
    // aim point) so the marker truthfully brackets the target the Maverick will
    // follow. Only while aiming — that's the only state from which a round can
    // launch. Project the entity's world centre into the seeker camera; show the
    // marker only when it's in front of the optic and inside the frame.
    this.lockedEntity = this.aiming
      ? this._nearestEnemyTo(this.losPoint, AGM65.lockAcquireRadius)
      : null;

    let lockActive = 0;
    if (this.lockedEntity) {
      const c = this.lockedEntity.hitVolume?.center ?? this.lockedEntity.flight?.position;
      if (c && _toTarget.copy(c).sub(this.seekerCamera.position).dot(_dir) > 0) {
        _proj.copy(c).project(this.seekerCamera);
        if (_proj.x >= -1 && _proj.x <= 1 && _proj.y >= -1 && _proj.y <= 1) {
          // NDC → plane UV. The shader samples the feed flipped in V
          // (fuv = vUv with V inverted), so the marker's V is flipped to match.
          this._lockUv.set(_proj.x * 0.5 + 0.5, 0.5 - _proj.y * 0.5);
          lockActive = 1;
        }
      }
    }

    // ── TV material + visibility ─────────────────────────────────────────
    if (this.tvMaterial) {
      this.tvMaterial.uniforms.uTime.value += dt;
      this.tvMaterial.uniforms.uCrossGap.value = this._crossGap;
      this.tvMaterial.uniforms.uLockActive.value = lockActive;
    }
    if (this.tvMesh) this.tvMesh.visible = true; // cockpitHD visibility hides it outside cockpit

    this._shouldRenderTV = this._cockpitActive;
  }

  // ── Offscreen render of the seeker view (called from App before composer) ──

  /**
   * Render the seeker's IR feed into the two render targets. Two passes:
   *   1. base — the full scene from the seeker camera (greyscaled in the shader).
   *   2. hot  — enemies only, flat white on black (composited over the base).
   * Gated to when the Maverick is selected with a round mounted and the cockpit
   * view is active, so it costs nothing the rest of the time.
   */
  renderTV(renderer) {
    if (!this.active || !this._shouldRenderTV) return;

    this._tagEnemiesHot();

    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    const prevAutoClearDepth = renderer.autoClearDepth;
    const prevShadowAuto = renderer.shadowMap.autoUpdate;
    renderer.getClearColor(_clearColor);
    const prevAlpha = renderer.getClearAlpha();
    const prevOverride = this.scene.overrideMaterial;
    const prevBackground = this.scene.background;
    const aircraftGroup = this.aircraft.group;
    const prevAircraftVisible = aircraftGroup.visible;

    renderer.autoClear = true;
    renderer.shadowMap.autoUpdate = false; // reuse the main pass's shadow map (cheap, fine for the low-res feed)
    aircraftGroup.visible = false; // keep the A-10's own geometry out of the feed

    // Pass 1 — base scene (keeps the sky background for natural framing). This
    // also fills the shared depth texture with the full scene's depth. The FX
    // overlay layer comes along: this is a plain forward render with no cloud
    // pass, so the FX parked there for the main frame would otherwise just vanish
    // from the feed (smoke and fires are half of what the TV is aimed at).
    this.seekerCamera.layers.set(0);
    this.seekerCamera.layers.enable(FX_OVERLAY_LAYER);
    this.scene.overrideMaterial = null;
    renderer.setRenderTarget(this.rtBase);
    renderer.render(this.scene, this.seekerCamera);

    // Pass 2 — hot enemies, flat white on a black field. Clear only colour, not
    // depth, so the pass-1 scene depth survives and occludes enemies that sit
    // behind terrain or buildings.
    renderer.autoClearDepth = false;
    this.seekerCamera.layers.set(HOT_LAYER);
    this.scene.overrideMaterial = this._whiteMaterial;
    this.scene.background = null;
    renderer.setClearColor(0x000000, 1);
    renderer.setRenderTarget(this.rtHot);
    renderer.render(this.scene, this.seekerCamera);

    // Restore renderer + scene state.
    this.scene.overrideMaterial = prevOverride;
    this.scene.background = prevBackground;
    this.seekerCamera.layers.set(0);
    aircraftGroup.visible = prevAircraftVisible;
    renderer.setClearColor(_clearColor, prevAlpha);
    renderer.autoClear = prevAutoClear;
    renderer.autoClearDepth = prevAutoClearDepth;
    renderer.shadowMap.autoUpdate = prevShadowAuto;
    renderer.setRenderTarget(prevTarget);
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  /** Nearest live enemy whose centre is within `radius` of `point`, or null. */
  _nearestEnemyTo(point, radius) {
    const ents = this.enemyManager?.entities;
    if (!ents) return null;
    let best = null;
    let bestSq = radius * radius;
    for (let i = 0; i < ents.length; i++) {
      const e = ents[i];
      if (!e || e.dead || e.destroyed) continue;
      const c = e.hitVolume?.center ?? e.flight?.position;
      if (!c) continue;
      const dSq = _tp.copy(c).sub(point).lengthSq();
      if (dSq < bestSq) { bestSq = dSq; best = e; }
    }
    return best;
  }

  /** Enable the hot render layer on any newly-spawned enemy meshes. */
  _tagEnemiesHot() {
    const ents = this.enemyManager?.entities;
    if (!ents) return;
    for (let i = 0; i < ents.length; i++) {
      const e = ents[i];
      if (!e || this._hotTagged.has(e)) continue;
      const root = e.group || e.modelRoot;
      if (!root) continue;
      root.traverse((child) => { if (child.isMesh) child.layers.enable(HOT_LAYER); });
      this._hotTagged.add(e);
    }
  }

  dispose() {
    if (this.tvMesh && this._originalTvMaterial) {
      this.tvMesh.material = this._originalTvMaterial;
      this.tvMesh.visible = true;
    }
    this.tvMaterial?.dispose();
    this._whiteMaterial.dispose();
    this.rtBase.dispose();
    this.rtHot.dispose();
    this._depthTexture.dispose();
  }
}
